import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import Reveal from "@/components/site/Reveal";

const ANNIKA = "/images/annika-editorial.jpg";
const JULIA  = "/images/julia-editorial.jpg";

const dozentinnen = [
  {
    name: "Annika Lorke",
    role: "Leitung Lehrgänge",
    img: ANNIKA,
    frame: "#2A2320",
    rotate: "-1deg",
    pos: "50% 30%",
    zoom: 1.22,
    qualifications: [
      "LG1 & LG2 Behandlungspflege",
      "Pflegehilfskraft & Betreuung",
      "Ambulante Pflegeassistenz",
    ],
    quote: "Pflege lernt man nicht aus Büchern — Pflege lernt man von Menschen, die sie leben.",
  },
  {
    name: "Julia Müller",
    role: "Leitung PDL & Fachweiterbildungen",
    img: JULIA,
    frame: "#C6A478",
    rotate: "1deg",
    pos: "50% 30%",
    zoom: 1.22,
    qualifications: [
      "Pflegedienstleitung (PDL)",
      "Praxisanleitung",
      "Wundexpertin",
      "Palliative Care",
      "Pflegeberater §7a SGB XI",
    ],
    quote: "Mein Anspruch: Wissen, das morgen schon am Patientenbett wirkt.",
  },
];

export default function Team() {
  return (
    <section id="team" className="relative py-32 md:py-44 overflow-hidden" style={{ backgroundColor: "#F6E8DC" }}>
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <div className="max-w-3xl mb-20">
            <div className="flex items-center gap-3 mb-4">
              <span className="h-px w-10 bg-terra" />
              <span className="font-poster text-[11px] uppercase tracking-[0.3em] font-medium text-terra">
                Unsere Dozentinnen
              </span>
            </div>
            <h2 className="font-magazine font-medium text-[2.6rem] sm:text-[3.6rem] lg:text-[4.4rem] text-sand-800 leading-[1.05] tracking-tight">
              Von Menschen lernen, <br className="hidden sm:block" />
              <span className="text-terra italic">die Pflege kennen.</span>
            </h2>
            <p className="mt-7 text-sand-600 text-base sm:text-lg max-w-2xl leading-[1.7]">
              Unsere Dozentinnen kommen aus der Praxis — und nehmen sich Zeit für dich.
            </p>
          </div>
        </Reveal>

        {/* Portraits im Galerie-Rahmen · freundlich & luftig */}
        <div className="grid md:grid-cols-2 gap-x-12 lg:gap-x-24 gap-y-20">
          {dozentinnen.map((d, idx) => (
            <Reveal key={d.name} delay={idx * 140}>
              <article className={`group grid sm:grid-cols-[minmax(0,260px)_1fr] gap-8 sm:gap-9 items-start ${idx === 1 ? "md:mt-16" : ""}`}>
                {/* Galerie-Rahmen wie PosterWall */}
                <div className="max-w-[260px]" style={{ rotate: d.rotate }}>
                  <div
                    className="p-[7px] shadow-[0_30px_60px_-24px_rgba(27,26,24,0.4),0_8px_18px_-10px_rgba(27,26,24,0.2)] group-hover:-translate-y-2 transition-transform duration-300"
                    style={{ backgroundColor: d.frame }}
                  >
                    <div className="bg-white p-3">
                      <div className="aspect-[3/4] overflow-hidden">
                        <img
                          src={d.img}
                          alt={`${d.name} — ${d.role}`}
                          loading="lazy"
                          className="w-full h-full object-cover"
                          style={{
                            objectPosition: d.pos,
                            transform: d.zoom ? `scale(${d.zoom})` : undefined,
                            transformOrigin: "50% 42%",
                          }}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 text-center font-poster uppercase tracking-[0.3em] text-sand-800/40 text-[9px]">
                    N° 0{idx + 1} · {d.name.split(" ")[0]}
                  </div>
                </div>

                {/* Bio */}
                <div>
                  <div className="font-poster text-[10.5px] uppercase tracking-[0.3em] font-medium text-terra">
                    {d.role}
                  </div>
                  <h3 className="mt-2 font-magazine text-[1.9rem] md:text-[2.2rem] text-sand-800 font-medium leading-tight">
                    {d.name}
                  </h3>

                  <div className="mt-5 flex flex-wrap gap-2">
                    {d.qualifications.map((q) => (
                      <span
                        key={q}
                        className="inline-flex items-center rounded-full bg-white/70 border border-terra/25 px-3.5 py-1.5 text-[12.5px] text-sand-700"
                      >
                        {q}
                      </span>
                    ))}
                  </div>

                  <blockquote className="mt-7 font-magazine italic text-[1.15rem] md:text-[1.25rem] text-sand-700 leading-relaxed">
                    „{d.quote}“
                    <span className="mt-3 block h-[2px] w-10 bg-saffron" aria-hidden="true" />
                  </blockquote>
                </div>
              </article>
            </Reveal>
          ))}
        </div>

        {/* CTA · freundlich */}
        <Reveal delay={250}>
          <div className="mt-24 bg-cream-50 border border-sand-800/10 shadow-[0_24px_60px_-36px_rgba(82,42,37,0.35)] px-8 py-10 md:px-12 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
            <div>
              <h3 className="font-magazine text-2xl md:text-3xl text-sand-800 font-normal">
                Lern Annika oder Julia <em className="italic text-terra">persönlich</em> kennen.
              </h3>
              <p className="mt-2 text-sand-600 text-sm md:text-base max-w-xl">
                Unverbindliches Erstgespräch – telefonisch oder per Video. Wir nehmen uns Zeit.
              </p>
            </div>
            <Button asChild variant="outline" className="rounded-full border-[1.5px] border-terra text-terra hover:bg-terra hover:text-cream-50 bg-transparent h-12 px-8 font-poster text-[13px] uppercase tracking-[0.18em] font-medium group whitespace-nowrap transition-colors">
              <a href="#kontakt">
                Kostenlose Beratung
                <ArrowRight className="ml-2 h-4 w-4 nudge" strokeWidth={1.8} />
              </a>
            </Button>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
